import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { useToast } from '../hooks/useToast'

interface CalendarEvent {
  id: string
  title: string
  description?: string
  start: string
  end: string
  type: 'scheduled_work' | 'system_task' | 'deadline' | 'maintenance' | 'other'
  ticketId?: string
  ticketNumber?: string
  priority?: string
  assignedTo?: string
  clientOrg?: string
  isAllDay: boolean
  status?: string
}

interface CalendarDay {
  date: Date
  events: CalendarEvent[]
  isToday: boolean
  isCurrentMonth: boolean
}

interface EventCreationModalProps {
  selectedDate: Date | null
  onClose: () => void
  onEventCreated: (event: CalendarEvent) => void
}

const EventCreationModal: React.FC<EventCreationModalProps> = ({ selectedDate, onClose, onEventCreated }) => {
  const { showError } = useToast()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start: selectedDate ? selectedDate.toISOString().slice(0, 16) : '',
    end: '',
    type: 'other' as CalendarEvent['type'],
    priority: 'Medium',
    isAllDay: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      showError('Please enter a title for the event')
      return
    }

    if (!formData.start) {
      showError('Please select a start date and time')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await axios.post('/api/staff/calendar/events', {
        title: formData.title,
        description: formData.description,
        start: formData.start,
        end: formData.end || undefined,
        type: formData.type,
        priority: formData.priority,
        isAllDay: formData.isAllDay
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })

      if (response.data.success) {
        onEventCreated(response.data.data)
      } else {
        showError('Failed to create event')
      }
    } catch (error) {
      console.error('Error creating event:', error)
      showError('Failed to create event')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Event</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Event title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Event description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
                <input
                  type="datetime-local"
                  value={formData.start}
                  onChange={(e) => handleInputChange('start', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                <input
                  type="datetime-local"
                  value={formData.end}
                  onChange={(e) => handleInputChange('end', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="other">Other</option>
                  <option value="system_task">System Task</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="deadline">Deadline</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isAllDay"
                checked={formData.isAllDay}
                onChange={(e) => handleInputChange('isAllDay', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isAllDay" className="ml-2 block text-sm text-gray-700">
                All day event
              </label>
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

const Calendar: React.FC = () => {
  const { user } = useAuthStore()
  const { showError, showSuccess } = useToast()
  const navigate = useNavigate()
  
  const [currentDate, setCurrentDate] = useState(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'month' | 'week' | 'day'>('month')
  const [showEventModal, setShowEventModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  useEffect(() => {
    fetchEvents()
  }, [currentDate])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/staff/calendar', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      
      if (response.data.success) {
        setEvents(response.data.data)
      } else {
        // For now, use sample data
        setEvents(getSampleEvents())
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error)
      // Use sample data on error
      setEvents(getSampleEvents())
    } finally {
      setLoading(false)
    }
  }

  const getSampleEvents = (): CalendarEvent[] => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const nextWeek = new Date(today)
    nextWeek.setDate(nextWeek.getDate() + 7)

    return [
      {
        id: '1',
        title: 'Server Migration - Local Home Valets',
        description: 'Migrate client server to new infrastructure',
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 10, 0).toISOString(),
        end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 14, 0).toISOString(),
        type: 'scheduled_work',
        ticketId: 'TKT-001',
        ticketNumber: 'TKT-001',
        priority: 'High',
        assignedTo: 'John Smith',
        clientOrg: 'Local Home Valets Ltd',
        isAllDay: false,
        status: 'Work Scheduled'
      },
      {
        id: '2',
        title: 'Database Backup Verification',
        description: 'Verify automated backup systems are working correctly',
        start: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 9, 0).toISOString(),
        end: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 10, 0).toISOString(),
        type: 'system_task',
        priority: 'Medium',
        assignedTo: 'System',
        isAllDay: false,
        status: 'Scheduled'
      },
      {
        id: '3',
        title: 'SSL Certificate Renewal',
        description: 'Renew SSL certificates for production domains',
        start: new Date(nextWeek.getFullYear(), nextWeek.getMonth(), nextWeek.getDate()).toISOString(),
        end: new Date(nextWeek.getFullYear(), nextWeek.getMonth(), nextWeek.getDate()).toISOString(),
        type: 'maintenance',
        priority: 'Critical',
        assignedTo: 'Sarah Johnson',
        isAllDay: true,
        status: 'Work Scheduled'
      }
    ]
  }

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'scheduled_work': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'system_task': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'deadline': return 'bg-red-100 text-red-800 border-red-200'
      case 'maintenance': return 'bg-orange-100 text-orange-800 border-orange-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'scheduled_work': return '🔧'
      case 'system_task': return '⚙️'
      case 'deadline': return '⏰'
      case 'maintenance': return '🛠️'
      default: return '📅'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-green-600'
      default: return 'text-gray-600'
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getDaysInMonth = (date: Date): CalendarDay[] => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    const days: CalendarDay[] = []
    const today = new Date()
    
    for (let i = 0; i < 42; i++) {
      const currentDay = new Date(startDate)
      currentDay.setDate(startDate.getDate() + i)
      
      const dayEvents = events.filter(event => {
        const eventDate = new Date(event.start)
        return eventDate.toDateString() === currentDay.toDateString()
      })
      
      days.push({
        date: currentDay,
        events: dayEvents,
        isToday: currentDay.toDateString() === today.toDateString(),
        isCurrentMonth: currentDay.getMonth() === month
      })
    }
    
    return days
  }

  const getDaysInWeek = (date: Date): CalendarDay[] => {
    const startOfWeek = new Date(date)
    startOfWeek.setDate(date.getDate() - date.getDay())
    
    const days: CalendarDay[] = []
    const today = new Date()
    
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek)
      currentDay.setDate(startOfWeek.getDate() + i)
      
      const dayEvents = events.filter(event => {
        const eventDate = new Date(event.start)
        return eventDate.toDateString() === currentDay.toDateString()
      })
      
      days.push({
        date: currentDay,
        events: dayEvents,
        isToday: currentDay.toDateString() === today.toDateString(),
        isCurrentMonth: true // Always show current month in week view
      })
    }
    
    return days
  }

  const getDayEvents = (date: Date): CalendarEvent[] => {
    return events.filter(event => {
      const eventDate = new Date(event.start)
      return eventDate.toDateString() === date.toDateString()
    })
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    setCurrentDate(newDate)
  }

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 7)
    } else {
      newDate.setDate(newDate.getDate() + 7)
    }
    setCurrentDate(newDate)
  }

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1)
    } else {
      newDate.setDate(newDate.getDate() + 1)
    }
    setCurrentDate(newDate)
  }

  const getTodaysEvents = () => {
    const today = new Date()
    return events.filter(event => {
      const eventDate = new Date(event.start)
      return eventDate.toDateString() === today.toDateString()
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Work Schedule Calendar</h1>
          <p className="mt-2 text-sm text-gray-700">
            Welcome back, {user?.firstName}! Here's your scheduled work and system tasks
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex space-x-3">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setView('month')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                view === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-2 text-sm font-medium ${
                view === 'week' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setView('day')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                view === 'day' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Day
            </button>
          </div>
          <button
            onClick={() => {
              setSelectedDate(day.date)
              setShowEventModal(true)
            }}
            className="btn-primary"
          >
            Add Event
          </button>
        </div>
      </div>

      {/* Today's Events */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Today's Events</h2>
        <div className="bg-white rounded-lg shadow">
          {getTodaysEvents().length > 0 ? (
            <div className="divide-y divide-gray-200">
              {getTodaysEvents().map((event) => (
                <div 
                  key={event.id} 
                  className={`p-4 hover:bg-gray-50 ${event.ticketId ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (event.ticketId) {
                      navigate(`/tickets/${event.ticketId}`)
                    }
                  }}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-lg">{getEventTypeIcon(event.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium text-gray-900">{event.title}</h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getEventTypeColor(event.type)}`}>
                          {event.type.replace('_', ' ')}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      )}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>🕐 {formatTime(event.start)} - {formatTime(event.end)}</span>
                        {event.priority && (
                          <span className={`font-medium ${getPriorityColor(event.priority)}`}>
                            {event.priority.toUpperCase()}
                          </span>
                        )}
                        {event.assignedTo && (
                          <span>👤 {event.assignedTo}</span>
                        )}
                        {event.ticketNumber && (
                          <span className="text-blue-600 font-medium hover:text-blue-800">{event.ticketNumber}</span>
                        )}
                        {event.ticketId && (
                          <span className="text-xs text-blue-500">Click to view ticket</span>
                        )}
                        {event.clientOrg && (
                          <span>🏢 {event.clientOrg}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-400 text-4xl mb-2">📅</div>
              <p className="text-gray-500">No events scheduled for today</p>
            </div>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      {view === 'month' && (
        <div className="bg-white rounded-lg shadow">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                ←
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                Today
              </button>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                →
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth(currentDate).map((day, index) => (
                <div
                  key={index}
                  className={`min-h-[100px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${day.isToday ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => {
                    setSelectedDate(day.date)
                    setShowEventModal(true)
                  }}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  } ${day.isToday ? 'text-blue-600' : ''}`}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {day.events.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        className={`text-xs p-1 rounded truncate cursor-pointer hover:opacity-80 ${getEventTypeColor(event.type)}`}
                        title={`${event.title}${event.ticketNumber ? ` (${event.ticketNumber})` : ''}${event.priority ? ` - ${event.priority}` : ''}`}
                        onClick={(e) => {
                          e.stopPropagation() // Prevent day click
                          if (event.ticketId && event.type === 'scheduled_work') {
                            navigate(`/tickets/${event.ticketId}`)
                          }
                        }}
                      >
                        {getEventTypeIcon(event.type)} {event.title}
                        {event.ticketNumber && (
                          <div className="text-xs font-medium text-blue-600">{event.ticketNumber}</div>
                        )}
                      </div>
                    ))}
                    {day.events.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{day.events.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div className="bg-white rounded-lg shadow">
          {/* Week Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {(() => {
                const weekDays = getDaysInWeek(currentDate)
                const startDate = weekDays[0].date
                const endDate = weekDays[6].date
                if (startDate.getMonth() === endDate.getMonth()) {
                  return `${startDate.toLocaleDateString('en-GB', { month: 'long', day: 'numeric' })} - ${endDate.getDate()}, ${startDate.getFullYear()}`
                } else {
                  return `${startDate.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}, ${startDate.getFullYear()}`
                }
              })()}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => navigateWeek('prev')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                ←
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                Today
              </button>
              <button
                onClick={() => navigateWeek('next')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                →
              </button>
            </div>
          </div>

          {/* Week Grid */}
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {getDaysInWeek(currentDate).map((day, index) => (
                <div key={index} className="text-center">
                  <div className="text-sm font-medium text-gray-500 py-2">
                    {day.date.toLocaleDateString('en-GB', { weekday: 'short' })}
                  </div>
                  <div className={`text-lg font-semibold py-2 ${
                    day.isToday ? 'text-blue-600 bg-blue-50 rounded-full' : 'text-gray-900'
                  }`}>
                    {day.date.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Week Days */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInWeek(currentDate).map((day, index) => (
                <div
                  key={index}
                  className={`min-h-[200px] p-3 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
                    day.isToday ? 'ring-2 ring-blue-500 bg-blue-50' : 'bg-white'
                  }`}
                  onClick={() => {
                    setSelectedDate(day.date)
                    setShowEventModal(true)
                  }}
                >
                  <div className="space-y-2">
                    {day.events.map((event) => (
                      <div
                        key={event.id}
                        className={`text-xs p-2 rounded cursor-pointer hover:opacity-80 ${getEventTypeColor(event.type)}`}
                        title={`${event.title}${event.ticketNumber ? ` (${event.ticketNumber})` : ''}${event.priority ? ` - ${event.priority}` : ''}`}
                        onClick={(e) => {
                          e.stopPropagation() // Prevent day click
                          if (event.ticketId && event.type === 'scheduled_work') {
                            navigate(`/tickets/${event.ticketId}`)
                          }
                        }}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{getEventTypeIcon(event.type)}</span>
                          <span className="font-medium truncate">{event.title}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {formatTime(event.start)} - {formatTime(event.end)}
                        </div>
                        {event.ticketNumber && (
                          <div className="text-xs font-medium text-blue-600">{event.ticketNumber}</div>
                        )}
                        {event.priority && (
                          <div className={`text-xs font-medium ${getPriorityColor(event.priority)}`}>
                            {event.priority.toUpperCase()}
                          </div>
                        )}
                      </div>
                    ))}
                    {day.events.length === 0 && (
                      <div className="text-xs text-gray-400 text-center py-4">
                        No events
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Day View */}
      {view === 'day' && (
        <div className="bg-white rounded-lg shadow">
          {/* Day Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {formatDate(currentDate)}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => navigateDay('prev')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                ←
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                Today
              </button>
              <button
                onClick={() => navigateDay('next')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                →
              </button>
            </div>
          </div>

          {/* Day Content */}
          <div className="p-4">
            {(() => {
              const dayEvents = getDayEvents(currentDate)
              return dayEvents.length > 0 ? (
                <div className="space-y-4">
                  {dayEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${getEventTypeColor(event.type)}`}
                      onClick={() => {
                        if (event.ticketId && event.type === 'scheduled_work') {
                          navigate(`/tickets/${event.ticketId}`)
                        }
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{getEventTypeIcon(event.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getEventTypeColor(event.type)}`}>
                              {event.type.replace('_', ' ')}
                            </span>
                            {event.priority && (
                              <span className={`text-sm font-medium ${getPriorityColor(event.priority)}`}>
                                {event.priority.toUpperCase()}
                              </span>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                          )}
                          <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <span>🕐 {formatTime(event.start)} - {formatTime(event.end)}</span>
                            {event.assignedTo && (
                              <span>👤 {event.assignedTo}</span>
                            )}
                            {event.ticketNumber && (
                              <span className="text-blue-600 font-medium hover:text-blue-800">{event.ticketNumber}</span>
                            )}
                            {event.clientOrg && (
                              <span>🏢 {event.clientOrg}</span>
                            )}
                            {event.ticketId && (
                              <span className="text-xs text-blue-500">Click to view ticket</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">📅</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No events scheduled</h3>
                  <p className="text-gray-500 mb-4">No events are scheduled for this day.</p>
                  <button
                    onClick={() => {
                      setSelectedDate(currentDate)
                      setShowEventModal(true)
                    }}
                    className="btn-primary"
                  >
                    Add Event
                  </button>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Event Creation Modal */}
      {showEventModal && (
        <EventCreationModal
          selectedDate={selectedDate}
          onClose={() => setShowEventModal(false)}
          onEventCreated={(newEvent) => {
            setEvents(prev => [...prev, newEvent])
            setShowEventModal(false)
            showSuccess('Event created successfully!')
          }}
        />
      )}
    </div>
  )
}

export default Calendar
